import { usePostHogCapture } from '@app/core/integrations/posthog/usePostHogCapture';
import { PersonAppearance, PersonLane } from '@app/modules/persons/appearances/types';
import { useCallback, useMemo } from 'react';

/** Where the Personas interaction happened (track vs results-tab detail). */
export type PersonsCaptureSurface = 'track' | 'results_table';

/** Builds the shared PostHog payload from a lane + appearance interval. */
function toCaptureProps(
  lane: PersonLane,
  appearance: PersonAppearance,
  surface: PersonsCaptureSurface,
) {
  return {
    person_id: lane.personId,
    lane_key: lane.laneKey,
    source: appearance.source,
    is_unknown: lane.isUnknown,
    surface,
  };
}

/**
 * Centralizes PostHog events for Personas (track bars/popover + results table).
 * Pass `surface: 'results_table'` from the results detail so events are distinct
 * from the track (`surface: 'track'`, the default).
 */
export function usePersonsTrackCapture() {
  const capture = usePostHogCapture({ domain: 'persons' });

  const appearanceBarClicked = useCallback(
    (lane: PersonLane, appearance: PersonAppearance, surface: PersonsCaptureSurface = 'track') => {
      capture('appearance_bar_clicked', toCaptureProps(lane, appearance, surface));
    },
    [capture],
  );

  const playFromAppearanceClicked = useCallback(
    (lane: PersonLane, hovered: PersonAppearance, surface: PersonsCaptureSurface = 'track') => {
      capture('play_from_appearance_clicked', toCaptureProps(lane, hovered, surface));
    },
    [capture],
  );

  const copyLinkClicked = useCallback(
    (lane: PersonLane, hovered: PersonAppearance, surface: PersonsCaptureSurface = 'track') => {
      capture('copy_link_clicked', toCaptureProps(lane, hovered, surface));
    },
    [capture],
  );

  return useMemo(
    () => ({
      appearanceBarClicked,
      playFromAppearanceClicked,
      copyLinkClicked,
    }),
    [appearanceBarClicked, playFromAppearanceClicked, copyLinkClicked],
  );
}
